import { z } from "zod";

import { LeadScoringConflictCheckResult } from "@signalist/shared-types";

import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	Score,
	ScoreResponse,
	ScoreSchema,
	TestCompanyCategoryResultsResponse,
	TestCompanyCategoryResultsResponseSchema,
	TestPersonaCategoryResultsResponseSchema,
	TestPersonaCategoryResultsResponse,
	ScoreCriteriaResponseSchema,
	ScoreCriteriaResponse,
	ScoreCriteriaDuplicate,
	CompanyCategoryResultHistoryResponse,
	CompanyCategoryResultHistoryResponseSchema,
	PersonaCategoryResultHistoryResponse,
	PersonaCategoryResultHistoryResponseSchema,
	ScoringHistoryResponse,
	ScoringHistoryResponseSchema,
	LeadScoringConflictCheckResultSchema,
} from "../../pages/Score/schema/score.schema";

import { normalizeScoreCriteriaResponse } from "./normalizeScoreCriteria";

export const scoreApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getScore: builder.query<ScoreResponse, void>({
			query: () => ({
				url: "score",
				responseSchema: ScoreSchema,
			}),
			providesTags: ["Score"],
		}),
		getTestCompanyCategoryResults: builder.query<
			TestCompanyCategoryResultsResponse,
			RequestPaginationParams
		>({
			query: ({ ...params }) => ({
				url: "score/test-company-category-results",
				params: {
					...(params && { ...params }),
				},
				responseSchema: TestCompanyCategoryResultsResponseSchema,
			}),
		}),
		getTestLeadScoringLogicResults: builder.query<
			TestPersonaCategoryResultsResponse,
			RequestPaginationParams
		>({
			query: ({ ...params }) => ({
				url: "score/test-lead-scoring-logic-results",
				params: {
					...(params && { ...params }),
				},
				responseSchema: TestPersonaCategoryResultsResponseSchema,
			}),
		}),
		getTestPersonaCategoryResults: builder.query<
			TestPersonaCategoryResultsResponse,
			RequestPaginationParams
		>({
			query: ({ ...params }) => ({
				url: "score/test-persona-category-results",
				params: {
					...(params && { ...params }),
				},
				responseSchema: TestPersonaCategoryResultsResponseSchema,
			}),
		}),
		getScoreCriteria: builder.query<ScoreCriteriaResponse, void>({
			query: () => ({
				url: "score/criteria",
				responseSchema: ScoreCriteriaResponseSchema,
			}),
			// Heals AI-written OTHER groups arriving as a single object (PREC-3700).
			transformResponse: normalizeScoreCriteriaResponse,
			providesTags: ["ScoreCriteria"],
		}),
		updateScoreCriteria: builder.mutation<ScoreCriteriaResponse, ScoreCriteriaResponse>({
			query: (body) => ({
				url: "score/criteria",
				method: "PATCH",
				body,
				responseSchema: ScoreCriteriaResponseSchema,
			}),
			transformResponse: normalizeScoreCriteriaResponse,
			invalidatesTags: ["ScoreCriteria"],
		}),
		createScore: builder.mutation<Partial<Score>, Partial<Score>>({
			query: (body) => ({
				url: `score`,
				method: "POST",
				body,
				responseSchema: ScoreSchema,
			}),
			invalidatesTags: ["Score"],
		}),
		updateScore: builder.mutation<Score, Score>({
			query: (body) => ({
				url: `score`,
				method: "PATCH",
				body: body,
				responseSchema: ScoreSchema,
			}),
			invalidatesTags: ["Score"],
		}),
		deleteScore: builder.mutation<Partial<Score>, number>({
			query: (id) => ({
				url: `score/company-category/${id}`,
				method: "DELETE",
				responseSchema: ScoreSchema,
			}),
			invalidatesTags: ["Score"],
		}),
		deletePersonaScore: builder.mutation<Partial<Score>, number>({
			query: (id) => ({
				url: `score/persona-category/${id}`,
				method: "DELETE",
				responseSchema: ScoreSchema,
			}),
			invalidatesTags: ["Score"],
		}),
		deleteLeadScoringLogic: builder.mutation<{ message: string }, number>({
			query: (id) => ({
				url: `score/criteria/${id}`,
				method: "DELETE",
				responseSchema: z.object({ message: z.string() }),
			}),
			invalidatesTags: ["ScoreCriteria"],
		}),
		createLeadScoringLogic: builder.mutation<
			Partial<ScoreCriteriaResponse>,
			{
				name: string;
				criteria?: ScoreCriteriaDuplicate["criteria"];
				scoreCriteriaCompaniesPersonas?: {
					scoreCompanyCategoryId: number | null;
					scorePersonaCategoryId: number;
					investor: string | null;
				}[];
			}
		>({
			query: (body) => ({
				url: `score/criteria`,
				method: "POST",
				body,
				responseSchema: ScoreCriteriaResponseSchema,
			}),
			invalidatesTags: ["ScoreCriteria"],
		}),
		checkCriteriaConflicts: builder.mutation<
			LeadScoringConflictCheckResult,
			{ criteria: ScoreCriteriaDuplicate["criteria"] }
		>({
			query: (body) => ({
				url: `score/criteria/conflict-check`,
				method: "POST",
				body,
				responseSchema: LeadScoringConflictCheckResultSchema,
			}),
		}),
		createCriteriaCompanyPersona: builder.mutation<
			Partial<ScoreCriteriaResponse>,
			{
				scoreCompanyCategoryId?: number;
				scorePersonaCategoryId: number;
				scoreCriteriaId: number;
				investor?: string;
			}
		>({
			query: (body) => ({
				url: `score/criteria-company-persona`,
				method: "POST",
				body,
				responseSchema: ScoreCriteriaResponseSchema,
			}),
			invalidatesTags: ["ScoreCriteria"],
		}),
		updateCriteriaCompanyPersona: builder.mutation<
			Partial<ScoreCriteriaResponse>,
			{
				scoreCompanyCategoryId?: number;
				scorePersonaCategoryId: number;
				scoreCriteriaId: number;
				investor?: string;
			}
		>({
			query: (body) => ({
				url: `score/criteria-company-persona`,
				method: "PATCH",
				body,
				responseSchema: ScoreCriteriaResponseSchema,
			}),
			invalidatesTags: ["ScoreCriteria"],
		}),
		deleteCriteriaCompanyPersona: builder.mutation<Partial<ScoreCriteriaResponse>, number>({
			query: (id) => ({
				url: `score/criteria-company-persona/${id}`,
				method: "DELETE",
				responseSchema: ScoreCriteriaResponseSchema,
			}),
			invalidatesTags: ["ScoreCriteria"],
		}),
		getCompanyCategoryResultHistory: builder.query<
			CompanyCategoryResultHistoryResponse,
			{ id: number; page?: number; limit?: number }
		>({
			query: ({ id, ...params }) => ({
				url: `score/company-category-result/${id}/history`,
				params,
				responseSchema: CompanyCategoryResultHistoryResponseSchema,
			}),
		}),
		getPersonaCategoryResultHistory: builder.query<
			PersonaCategoryResultHistoryResponse,
			{ id: number; page?: number; limit?: number }
		>({
			query: ({ id, ...params }) => ({
				url: `score/persona-category-result/${id}/history`,
				params,
				responseSchema: PersonaCategoryResultHistoryResponseSchema,
			}),
		}),
		getScoringHistory: builder.query<
			ScoringHistoryResponse,
			{ page?: number; limit?: number; searchValue?: string; dateFrom?: string; dateTo?: string }
		>({
			query: (params) => ({
				url: `score/scoring-history/timeline`,
				params,
				responseSchema: ScoringHistoryResponseSchema,
			}),
		}),
	}),
});

export const {
	useGetScoreQuery,
	useLazyGetScoreQuery,
	useUpdateScoreMutation,
	useDeleteScoreMutation,
	useCreateScoreMutation,
	useLazyGetTestCompanyCategoryResultsQuery,
	useLazyGetTestPersonaCategoryResultsQuery,
	useDeletePersonaScoreMutation,
	useGetScoreCriteriaQuery,
	useLazyGetScoreCriteriaQuery,
	useUpdateScoreCriteriaMutation,
	useCreateCriteriaCompanyPersonaMutation,
	useCheckCriteriaConflictsMutation,
	useUpdateCriteriaCompanyPersonaMutation,
	useDeleteCriteriaCompanyPersonaMutation,
	useCreateLeadScoringLogicMutation,
	useLazyGetTestLeadScoringLogicResultsQuery,
	useDeleteLeadScoringLogicMutation,
	useLazyGetCompanyCategoryResultHistoryQuery,
	useLazyGetPersonaCategoryResultHistoryQuery,
	useGetScoringHistoryQuery,
	useLazyGetScoringHistoryQuery,
} = scoreApi;
export const accountApiReducer = scoreApi.reducer;
export const accountApiMiddleware = scoreApi.middleware;
